import { useState } from "react";
import api from '../config/api';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import { CircularProgress, Stack } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { useTranslation } from 'react-i18next';
import LogoUploader from "../layouts/LogoUploader";

const ProjectModal = ({
  project,
  openModal,
  handleClose,
  type,
  setCounter,
  organizationId,
  authToken,
  showAlert,
}) => {
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(project?.imgPath || null);
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (type !== "delete") {
      const formEl = e.target.closest("form");
      const formData = new FormData(formEl);
      const entries = {};
      for (let [key, value] of formData.entries()) {
        entries[key] = value.trim();
      }

      const payload = {
        name: entries.name,
        type: entries.type ?? 'GPT',
        language: entries.language ?? 'es',
        customFields: {
          openAiKey: entries.openAiKey ?? '123123',
        },
      };

      if (type === "add") {
        try {
          const response = await api.post(
            `/v2/projects/${organizationId}`,
            payload,
            { headers: { authorization: `Bearer ${authToken}` } }
          );

          if (response.data.response.status_response === "OK") {
            showAlert(t('projects.projectCreateSuccess'), "info");
            setCounter((prev) => {
              return prev + 1;
            });
            setLoading(false);
            closeModal();
          } else {
            setLoading(false);
            showAlert(t('projects.projectCreateError'), "warning");
          }
        } catch (err) {
          console.error(err);
          setLoading(false);
          showAlert(t('projects.projectCreateError'), "warning");
        }
      } else {
        try {
          const response = await api.put(
            `/v2/projects?organizationId=${organizationId}&projectId=${project.uid}`,
            payload,
            { headers: { authorization: `Bearer ${authToken}` } }
          );

          if (response.data.response.status_response === "OK") {
            showAlert(t('projects.projectEditSuccess'), "info");
            setCounter((prev) => {
              return prev + 1;
            });
            setLoading(false);
            closeModal();
          } else {
            setLoading(false);
            showAlert(t('projects.projectEditError'), "warning");
          }
        } catch (err) {
          console.error(err);
          setLoading(false);
          showAlert(t('projects.projectEditError'), "warning");
        }
      }
    } else {
      try {
        let response = await api.delete(
          `/projects?organizationId=${organizationId}&projectId=${project.uid}`,
          {
            headers: { authorization: `${authToken}` },
          }
        );
        if (response.data.response.status_response === "OK") {
          showAlert(t('projects.projectDeleteSuccess'), "info");
          setLoading(false);
          setCounter((prev) => {
            return prev + 1;
          });
          closeModal();
        } else {
          showAlert(t('projects.projectDeleteError'), "warning");
          setLoading(false);
        }
      } catch (err) {
        console.error(err);
        showAlert(t('projects.projectDeleteError'), "warning");
        setLoading(false);
      }
    }
  };

  const closeModal = () => {
    handleClose();
  };

  return (
    <Modal
      disableScrollLock={true}
      open={openModal}
      onClose={closeModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          paddingBottom: 2,
          maxHeight: 720,
          overflowY: "auto",
        }}
      >
        <Container maxWidth="xl" sx={{ pr: "16px !important" }}>
          {type !== "delete" ? (
            <Grid
              component="form"
              onSubmit={handleSubmit}
              container
              spacing={1}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Grid item xs={12}>
                <Typography
                  variant="h5"
                  component="h5"
                  sx={{ textAlign: "center" }}
                >
                  {type === "add" ? t('projects.projectNew') : t('projects.projectEdit')}
                </Typography>
              </Grid>

              {type === "edit" && (
                <Grid item xs={12} sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 1 }}>
                  <LogoUploader
                    currentUrl={logoUrl}
                    uploadUrl={`/projects/logo`}
                    params={{ organizationId, projectId: project?.uid }}
                    onUploaded={(url) => { setLogoUrl(url); if (setCounter) setCounter((p) => p + 1); }}
                    size={84}
                    shape="square"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    {t('branding.projectLogo')}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12} sx={{ width: "100%" }}>
                <FormControl required fullWidth sx={{ mt: 2 }}>
                  <TextField
                    name="name"
                    label={t('projects.projectName')}
                    defaultValue={project?.name}
                    variant="outlined"
                    required
                    fullWidth
                    sx={{ width: "100%" }}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} sx={{ width: "100%" }}>
                <FormControl required fullWidth sx={{ mt: 2 }}>
                  <TextField
                    select
                    name="language"
                    label={t('language')}
                    defaultValue={project?.language || 'es'}
                    variant="outlined"
                    required
                    fullWidth
                    sx={{ width: "100%" }}
                  >
                    <MenuItem value="es">Español</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                  </TextField>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                  width: "100%" // Asegúrate de que el Grid item tenga ancho completo
                }}
              >
                <LoadingButton
                  type="submit" // Añade este atributo
                  variant="contained"
                  loading={loading}
                  loadingIndicator={t('saving')}
                  sx={{
                    alignSelf: "flex-end",
                    mt: 0.2,
                    mb: 0,
                    boxShadow: "none",
                    width: "75%",
                    minWidth: "300px" // Opcional: establecer un ancho mínimo
                  }}
                >
                  {type === "add" ? t('create') : t('save')}
                </LoadingButton>
              </Grid>
            </Grid>
          ) : (
            <Grid container columnSpacing={2} rowSpacing={4}>
              <Grid item xs={12}>
                <Typography
                  id="keep-mounted-modal-title"
                  variant="h6"
                  component="h2"
                  sx={{ m: "0 auto", textAlign: "center" }}
                >
                  {t('projects.projectDelete')}
                </Typography>

                <Alert severity="warning" sx={{ m: "1rem 0" }}>
                  {t('projects.projectDeleteConfirm')} &nbsp;
                  <Typography variant={"strong"} component={"strong"}>
                    {project?.name}?
                  </Typography>
                  &nbsp;?
                </Alert>
                <Stack
                  direction="row"
                  gap={"1rem"}
                  sx={{ mt: 3 }}
                  justifyContent={"center"}
                >
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    sx={{ backgroundColor: loading ? "gray" : "red" }}
                    disabled={loading}
                  >
                    {t('yes')}
                    {loading && (
                      <Box sx={{ display: "flex" }}>
                        <CircularProgress
                          sx={{ color: "#0288d1", ml: 2 }}
                          size={23}
                        />
                      </Box>
                    )}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={closeModal}
                    disabled={loading}
                  >
                    {t('no')}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          )}
        </Container>
      </Box>
    </Modal>
  );
};

export default ProjectModal;